import React, { useState } from "react"
import useTranslate from "../../../../hooks/useTranslate"
import { ReactComponent as ProgramIcon } from '../../../../assets/img/icons/shield.svg'
import { translate } from "../../../../selectors/translations"
import { ProgramTaskActionType, ProgramTaskConfigDto } from "../../../../sdk"
import useSWRMutation from "swr/mutation"
import { apiV2 } from "../../../../repositories/repository"
import { setSuccessMsg } from "../../../../store/Auth/action"
import { useDispatch } from "react-redux"
import { fetchError } from "../../../../store/Accounts/action"
import useSWR from "swr"
import { openModalWindow } from "../../../../store/ModalWindowData/action"

interface PatientProgramTasksConfigProps {
  patient: string
}

export function PatientProgramTasksConfig({patient}: PatientProgramTasksConfigProps) {
  const dispatch = useDispatch()
  const translateLocalazy = useTranslate()
  const [inProgram, setInProgram] = useState(false)
  const [config, setConfig] = useState<ProgramTaskConfigDto[]>([
    {
      action: ProgramTaskActionType.STEPS,
      startTarget: 5000,
      maxTarget: 10_000,
      increaseAmountPerLvl: 500,
      increaseLvlAfterDays: 7,
      currentLvl: 1,
      enabled: true,
      lockedByDoctor: false,
    },
    {
      action: ProgramTaskActionType.SLEEP,
      startTarget: 420,
      maxTarget: 540,
      increaseAmountPerLvl: 30,
      increaseLvlAfterDays: 7,
      currentLvl: 1,
      enabled: true,
      lockedByDoctor: false,
    },
    {
      action: ProgramTaskActionType.GAMES,
      startTarget: 10,
      maxTarget: 60,
      increaseAmountPerLvl: 10,
      increaseLvlAfterDays: 7,
      currentLvl: 1,
      enabled: true,
      lockedByDoctor: false,
    },
    {
      action: ProgramTaskActionType.SOCIAL_INTERACTION,
      startTarget: 30,
      maxTarget: 180,
      increaseAmountPerLvl: 15,
      increaseLvlAfterDays: 7,
      currentLvl: 1,
      enabled: true,
      lockedByDoctor: false,
    },
    {
      action: ProgramTaskActionType.STRESS,
      enabled: true,
      lockedByDoctor: false,
    },
    {
      action: ProgramTaskActionType.DIET,
      enabled: true,
      lockedByDoctor: false,
    }
  ])
  const configForAction = (action: ProgramTaskActionType) => config.find(e => e.action === action)
  const setConfigForAction = (action: ProgramTaskActionType, key: keyof ProgramTaskConfigDto, value: string | boolean) => {
    let newValue
    if (['enabled', 'lockedByDoctor'].includes(key)) {
      newValue = value
    } else {
      newValue = value === '' ? null : Math.max(Number(value), 1)
    }
    setConfig(config.map(e => e.action === action ? {...e, [key]: newValue} : e))
  }
  const currentTarget = (action: ProgramTaskActionType) => {
    const c = config.find(e => e.action === action)!
    const startTarget = c.startTarget ?? 0
    const increaseAmountPerLvl = c.increaseAmountPerLvl ?? 0
    const lvlModificator = (c.currentLvl ?? 1) - 1

    const result = Math.min(startTarget + increaseAmountPerLvl * lvlModificator, c.maxTarget ?? Infinity)
    if (action === ProgramTaskActionType.SLEEP) {
      const hours = (result / 60).toFixed(1)
      return `${result} (${hours} ${translateLocalazy('patient.administrate.configure-tasks.measurement-hours')})`
    }

    return result
  }

  const {isLoading} = useSWR(
    'api.game.getForPatient',
    () => apiV2.program.getForPatient(patient),
    {
      revalidateOnMount: true,
      dedupingInterval: 0,
      revalidateOnFocus: false,
      onSuccess: (data) => {
        if (data) {
          setInProgram(true)

          // just for the doctors view lvl starts from 1 instead of 0
          data.taskConfig.forEach(e => {
            return e.currentLvl = e.currentLvl! + 1
          })
          setConfig(data.taskConfig)
        }
      }
    }
  )

  const {trigger: deleteProgram} = useSWRMutation('api.program.delete', () =>
      apiV2.program.delete(patient),
    {
      onSuccess: () => {
        setInProgram(false)
        dispatch(setSuccessMsg(translateLocalazy('patient.administrate.configure-tasks.deleted-message')))
      },
      onError: (e) => dispatch(fetchError(e.message))
    })
  const {trigger: upsertProgram} = useSWRMutation(
    'api.program.upsert',
    () => {
      const data = config.map(e => ({...e, currentLvl: e.currentLvl! - 1}))

      return apiV2.program.upsert({
        patient,
        taskConfig: data
      })
    },
    {
      onSuccess: () => {
        setInProgram(true)
        dispatch(setSuccessMsg(translateLocalazy('patient.administrate.configure-tasks.saved-message')))
      },
      onError: (e) => dispatch(fetchError(e.message))
    }
  )

  const getMeasurementUnits = (action: ProgramTaskActionType) => {
    switch (action) {
      case ProgramTaskActionType.GAMES:
      case ProgramTaskActionType.SOCIAL_INTERACTION:
      case ProgramTaskActionType.SLEEP:
        return translateLocalazy('patient.administrate.configure-tasks.measurement-minutes')
      case ProgramTaskActionType.STEPS:
      case ProgramTaskActionType.STRESS:
      case ProgramTaskActionType.DIET:
        return ''
    }
  }

  return (
    <div className="card-box patient-info">
      <div className="medical-items__header">
        <div className="box-title">
          <i className="icon-holder patients-title_icon-holder"><ProgramIcon color="#ffffff"/></i>
          <h3>{translateLocalazy('patient.administrate.configure-tasks')}</h3>
        </div>
      </div>

      <div className="card_inputs-box">
        {!isLoading && ([
          [ProgramTaskActionType.STEPS, 'patient.administrate.configure-tasks.steps'],
          [ProgramTaskActionType.SLEEP, 'patient.administrate.configure-tasks.sleep'],
          [ProgramTaskActionType.GAMES, 'patient.administrate.configure-tasks.games'],
          [ProgramTaskActionType.SOCIAL_INTERACTION, 'patient.administrate.configure-tasks.social-interaction']] as [ProgramTaskActionType, string][])
          .map(([action, title]) => (
            <div key={action}>
              <div
                className="program-task-title">{translateLocalazy(title)} {getMeasurementUnits(action)}
                <div>
                  {translateLocalazy('patient.administrate.configure-tasks.current-target')}:
                  <span className="current-task-target"> {currentTarget(action)}</span>
                </div>
              </div>
              <div className="flex">
                <div className="input-holder flex-item item-sm-3 item-md-1">
                  <label>{translateLocalazy('patient.administrate.configure-tasks.enabled')}</label>
                  <input type="checkbox"
                         name="enabled"
                         checked={configForAction(action)?.enabled}
                         onChange={(e) => setConfigForAction(action, 'enabled', e.target.checked)}/>
                </div>
                <div className="input-holder flex-item item-sm-3 item-md-1">
                  <label>{translateLocalazy('patient.administrate.configure-tasks.locked')}</label>
                  <input type="checkbox"
                         name="locked-by-doctor"
                         checked={configForAction(action)?.lockedByDoctor}
                         onChange={(e) => setConfigForAction(action, 'lockedByDoctor', e.target.checked)}/>
                </div>
                <div className="input-holder flex-item item-sm-6 item-md-2">
                  <label>{translateLocalazy('patient.administrate.configure-tasks.start-target')}</label>
                  <input type="number"
                         name="start-target"
                         value={configForAction(action)?.startTarget}
                         onChange={(e) => setConfigForAction(action, 'startTarget', e.target.value)}/>
                </div>
                <div className="input-holder flex-item item-sm-6 item-md-2">
                  <label>{translateLocalazy('patient.administrate.configure-tasks.max-target')}</label>
                  <input type="number"
                         name="max-target"
                         value={configForAction(action)?.maxTarget}
                         onChange={(e) => setConfigForAction(action, 'maxTarget', e.target.value)}/>
                </div>
                <div className="input-holder flex-item item-sm-6 item-md-2">
                  <label>{translateLocalazy('patient.administrate.configure-tasks.increase-per-lvl')}</label>
                  <input type="number"
                         name="increase-amount-per-lvl"
                         value={configForAction(action)?.increaseAmountPerLvl}
                         onChange={(e) => setConfigForAction(action, 'increaseAmountPerLvl', e.target.value)}/>
                </div>
                <div className="input-holder flex-item item-sm-6 item-md-2">
                  <label>{translateLocalazy('patient.administrate.configure-tasks.days-streak')}</label>
                  <input type="number"
                         name="days-streak-to-lvl-up"
                         value={configForAction(action)?.increaseLvlAfterDays}
                         onChange={(e) => setConfigForAction(action, 'increaseLvlAfterDays', e.target.value)}/>
                </div>
                <div className="input-holder flex-item item-sm-6 item-md-2">
                  <label>{translateLocalazy('patient.administrate.configure-tasks.lvl')}</label>
                  <input type="number"
                         name="lvl"
                         value={configForAction(action)?.currentLvl}
                         onChange={(e) => setConfigForAction(action, 'currentLvl', e.target.value)}/>
                </div>
              </div>
            </div>
          ))}
        {!isLoading && ([
          [ProgramTaskActionType.STRESS, 'Stress'],
          [ProgramTaskActionType.DIET, 'Diet']] as [ProgramTaskActionType, string][])
          .map(([action, title]) => (
            <div key={action} className="w-100">
              <div
                className="program-task-title">{translateLocalazy(title)}
              </div>
              <div className="flex">
                <div className="input-holder flex-item item-sm-3 item-md-1">
                  <label>{translateLocalazy('patient.administrate.configure-tasks.enabled')}</label>
                  <input type="checkbox"
                         name="enabled"
                         checked={configForAction(action)?.enabled}
                         onChange={(e) => setConfigForAction(action, 'enabled', e.target.checked)}/>
                </div>
                <div className="input-holder flex-item item-sm-3 item-md-1">
                  <label>{translateLocalazy('patient.administrate.configure-tasks.locked')}</label>
                  <input type="checkbox"
                         name="locked-by-doctor"
                         checked={configForAction(action)?.lockedByDoctor}
                         onChange={(e) => setConfigForAction(action, 'lockedByDoctor', e.target.checked)}/>
                </div>
              </div>
            </div>
          ))}
      </div>

      <div className="btns-holder">
        {inProgram && <button
            onClick={() => dispatch(openModalWindow('confirmation', {
              onConfirm: () => deleteProgram(),
              text: translateLocalazy('patient.administrate.configure-tasks.delete-from-program')
            }))}
            className="g_btn t_uppercase g_btn--danger"
            type="button">{translateLocalazy('program.button.inactivate')}</button>}
        <button onClick={() => upsertProgram()}
                className="g_btn t_uppercase g_btn--primary"
                type="button">{inProgram ? translate('save') : translate('activateTestsInApp')}</button>
      </div>
    </div>
  )
}
